import Textarea from "components/form/Textarea/Textarea";
import Tooltip from "components/ui/Tooltip/Tooltip";
import { useState } from "react";
import Dialog from "components/ui/Dialog/Dialog";
import Trans from "features/translation/components/Trans";
import { useDialog } from "context/DialogContext";
import Button from "components/ui/Button/Button";
import { useAccount } from "context/AccountContext";
import Labels from "components/ui/Labels/Labels";
import Checkbox from "components/form/Checkbox/Checkbox";
import useData from "hooks/useData";
import ImageUpload from "components/form/ImageUpload/ImageUpload";
import Interests from "components/ui/Interests/Interests";
import { Link } from "react-router-dom";

const ProfileCreateUpdate = () => {
  const { hideDialog } = useDialog();
  const { profile, setProfile, refresh } = useAccount();

  const { patchData } = useData();
  const [ step, setStep ] = useState(1);

  const [ hideAge, setHideAge ] = useState(profile.field_profile_hide_age.value === '1');
  const [ showAgreementCheckbox, setAgreementCheckbox ] = useState(profile.field_profile_completed?.value === '1');
  const [ hideGender, setHideGender ] = useState(profile.field_profile_hide_gender.value === '1');
  const [ hideProfile, setHideProfile ] = useState(profile.field_profile_hide_profile?.value === '1');
  const [ hideVolunteer, setHideVolunteer ] = useState(profile.field_profile_hide_volunteer?.value === '1');
  const [ bio, setBio ] = useState(profile.field_profile_description.value);
  const [ photoData, setPhotoData ] = useState({});
  const [ interests, setInterests ] = useState(profile.field_interests.map(item => item.id));

  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState();

  const isNewProfile = !profile.field_profile_completed?.value || profile.field_profile_completed?.value === '0';

  let labels = [];
  if (!hideProfile) {
    labels.push(profile.title.value);

    if (!hideGender) {
      labels.push(profile.field_profile_gender.value);
    }

    if (profile.field_profile_city.value) {
      labels.push(profile.field_profile_city.value);
    }

    if (!hideAge) {
      if (profile.field_profile_age.value) {
        labels.push(`${ profile.field_profile_age.value } jaar`);
      }
      else {
        labels.push(`Leeftijd onbekend`);
      }
    }

    if (profile.field_profile_is_volunteer.value === '1' && !hideVolunteer) {
      labels.push('Vrijwilliger');
    }
  } else {
    labels.push('Onzichtbaar');
  }

  let checkbox;
  if (isNewProfile) {
    checkbox = <Checkbox label={ `Akkoord met de huisregels` }
                         checked={ showAgreementCheckbox }
                         onChange={ event => setAgreementCheckbox(event.target.checked) }>
      Ik ga akkoord met de <Link to={ '/huisregels' } target="_blank" rel="noopener noreferrer">huisregels</Link>
    </Checkbox>;
  }

  const steps = {
    1: (
      <>
        <div className={ 'dialog__row' }>
          <Trans
            text={ 'Vertel hier iets over uzelf en waarom u op het Trefpunt bent. Voeg eventueel een foto toe.' }/>
        </div>

        <div className={ 'dialog__row' }>
          <h3>Stel uw zichtbaarheid in</h3>
          <Checkbox label={ 'Mijn profiel verbergen' }
                    checked={ hideProfile }
                    onChange={ event => setHideProfile(event.target.checked) }>
            Mijn profiel verbergen
          </Checkbox>
          {
            !hideProfile &&
            <>
              <Checkbox label="Mijn leeftijd verbergen"
                        checked={ hideAge }
                        onChange={ event => setHideAge(event.target.checked) }>
                Mijn leeftijd verbergen
              </Checkbox>
              <Checkbox label="Mijn geslacht verbergen"
                        checked={ hideGender }
                        onChange={ event => setHideGender(event.target.checked) }>
                Mijn geslacht verbergen
              </Checkbox>
              {
                profile.field_profile_is_volunteer.value === '1' &&
                <Checkbox label="Mijn vrijwilligersfunctie verbergen"
                          checked={ hideVolunteer }
                          onChange={ event => setHideVolunteer(event.target.checked) }>
                  Mijn vrijwilligersfunctie verbergen
                </Checkbox>
              }
            </>
          }
        </div>

        <div className={ 'dialog__row' }>
          <h3 className={ 'flex--align-center' }>
            Uw gegevens
            <Tooltip message={ 'Deze gegevens zijn bij ons bekend omdat u lid bent van ANBO-PCOB.' }/>
          </h3>
          <Labels items={ labels } variant={ 'dark' }/>
        </div>

        <div className={ 'dialog__row' }>
          <h3>Over mij</h3>
          <Textarea onChange={ event => setBio(event.target.value) }
                    value={ bio }
                    placeholder={ 'Vertel kort iets meer over uzelf. Met wie zou u hier in contact willen komen? Geef aan waarvoor u hier aanwezig bent, bijv. rondkijken/ nieuwe mensen ontmoeten/iets ondernemen/om met mensen te chatten.' }/>
        </div>

        <div className={ 'dialog__row' }>
          <div className={ 'dialog__profile-picture' }>
            <ImageUpload title={ 'Profielfoto' }
                         subtitle={ '(niet verplicht)' }
                         type={ 'node' }
                         bundle={ 'profile' }
                         field={ 'field_profile_photo' }
                         currentData={ photoData }
                         currentValue={ profile.field_profile_photo.value }
                         onUploaded={ setPhotoData }
                         tooltipMessage={ 'Gebruik een afbeelding met de extensie .jpg of .png van maximaal 5 MB groot.' }/>
          </div>
        </div>
      </>
    ),
    2: (
      <>
        <div className={ 'dialog__row' }>
          <Trans text={ 'Hier kunt u per categorie uw interesses en hobby’s selecteren.' }/>
        </div>

        <div className={ 'dialog__row' }>
          <h3 className={ 'flex--align-center' }>Wat zijn uw interesses?
            <Tooltip message={ 'Mist u bepaalde interesses? Vertel hier dan meer over in het veld "Over mij".' }/>
          </h3>

          <Interests selection={ interests } onSelectionUpdated={ setInterests }/>
        </div>

        {
          isNewProfile &&
          <div className={ 'dialog__row' }>
            { checkbox }
          </div>
        }

      </>
    )
  };

  const onSave = async () => {
    setError();

    if (!showAgreementCheckbox) {
      setError(`U moet akkoord gaan met de huisregels van het platform`);
      return;
    }

    setSaving(true);

    const data = {
      ...profile,
      field_profile_completed: { value: '1' },
      field_profile_hide_profile: { value: hideProfile ? '1' : '0' },
      field_profile_hide_age: { value: hideAge ? '1' : '0' },
      field_profile_hide_gender: { value: hideGender ? '1' : '0' },
      field_profile_hide_volunteer: { value: hideVolunteer ? '1' : '0' },
      field_profile_description: { value: bio },
      field_profile_photo: { target_id: photoData && photoData.fid && photoData.fid[0].value },
      field_interests: interests.map(id => {
        return { target_id: id }
      }),
    };

    const result = await patchData(profile.id, 'profile', data);

    if (result?.status === 200) {
      const profile = await result.json();
      hideDialog();
      setProfile(profile);
      refresh();
    } else {
      setSaving(false);
      setError(`Het opslaan van het profiel is mislukt (${ result?.status })`);
    }
  };

  const onNext = () => {
    const nextStep = step + 1;
    if (Object.keys(steps).length >= nextStep) {
      setStep(nextStep);
    }
  };

  const onPrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <Dialog title={ isNewProfile ? 'Welkom bij Trefpunt!' : 'Profiel aanpassen' }
            loading={ saving }
            warnOnClose={ true }
            errorMessage={ error }
            scrollToTopIndicator={ step }>
      <>
        <h3>Stap { step } van { Object.keys(steps).length }</h3>
        { steps[step] }

        <div className={ `dialog__buttons ${ step === 1 ? 'dialog__buttons--end' : '' }` }>
          {
            step > 1 &&
            <Button onClick={ onPrevious } variant={ 'ghost' }>Vorige</Button>
          }

          { step === Object.keys(steps).length ?
            <Button onClick={ onSave } variant={ 'secondary' }>Opslaan</Button>
            :
            <Button onClick={ onNext } variant={ 'secondary' }>Naar stap { step + 1 }</Button>
          }
        </div>
      </>
    </Dialog>
  )
}

export default ProfileCreateUpdate;
